<template>
  <div class="folder-run-metrics" :class="{ 'folder-run-metrics--dense': dense }">
    <div v-if="!hasMetrics" class="folder-run-metrics__empty caption grey--text">
      <v-icon x-small left>mdi-label-off-outline</v-icon>
      No labelled videos in this run
    </div>

    <template v-else>
      <div class="folder-run-metrics__row">
        <div v-for="metric in tiles" :key="metric.key" class="folder-run-metrics__tile">
          <div
            class="folder-run-metrics__value"
            :class="`${metric.color}--text`"
            :title="metric.targetHint"
          >
            {{ metric.value }}
          </div>
          <div class="folder-run-metrics__label">{{ metric.label }}</div>
          <div
            v-if="metric.hasDelta"
            class="folder-run-metrics__delta"
            :class="metric.deltaClass"
            :title="deltaTitle"
          >
            <v-icon x-small :class="metric.deltaClass">{{ metric.deltaIcon }}</v-icon>
            {{ metric.delta }}
          </div>
          <div v-else-if="showDeltaSlot" class="folder-run-metrics__delta grey--text">—</div>
        </div>
      </div>

      <div v-if="counts" class="folder-run-metrics__counts caption grey--text">{{ counts }}</div>

      <!--
        The delta is never shown silently: a comparable/attributable delta names its conditions,
        and a non-attributable one keeps its number but greyed out and with the warning attached.
      -->
      <div
        v-if="band"
        class="folder-run-metrics__band caption"
        :class="band.muted ? 'grey--text' : `${band.color}--text`"
      >
        <v-icon x-small left :color="band.muted ? 'grey' : band.color">{{ band.icon }}</v-icon>
        <span v-if="againstLabel">{{ againstLabel }} · </span>{{ bandText }}
      </div>
    </template>
  </div>
</template>

<script>
import {
  FOLDER_RUN_METRICS,
  comparabilityBand,
  deltaColorClass,
  deltaIcon,
  formatDeltaPoints,
  formatPct,
  metricTargetColor,
  toNumber,
} from '@/utils/folderRuns';

/**
 * The three Coherence Evaluation metrics of a Folder Run plus, when the backend found a
 * comparable predecessor, their delta with its comparability treatment.
 *
 * These are the SAME metrics as the Coherence Evaluation screen (computed_subtraction), not new
 * ones, so the numbers here and there must always agree.
 */
export default {
  name: 'FolderRunMetrics',

  props: {
    /** FolderRunMetrics, or null when no video of the run is labelled. */
    metrics: { type: Object, default: null },
    /** The `delta` block of the summary (null when there is no comparable previous run). */
    delta: { type: Object, default: null },
    /** Compact variant used inside the list rows. */
    dense: { type: Boolean, default: false },
    /** Keeps the delta line's height even when there is no delta (keeps rows aligned). */
    showDeltaSlot: { type: Boolean, default: true },
    /** Caption naming the run the delta is measured against. */
    againstLabel: { type: String, default: '' },
  },

  computed: {
    hasMetrics() {
      return Boolean(this.metrics);
    },
    band() {
      return this.delta ? comparabilityBand(this.delta) : null;
    },
    bandText() {
      if (!this.band) return '';
      if (this.band.verdict === 'comparable') return 'Comparable with the previous run.';
      return this.band.text;
    },
    deltaTitle() {
      return this.band ? this.band.text : '';
    },
    tiles() {
      return FOLDER_RUN_METRICS.map((metric) => {
        const value = this.metrics ? this.metrics[metric.key] : null;
        const deltaValue = this.delta ? this.delta[metric.key] : null;
        const hasDelta = toNumber(deltaValue) !== null;
        return {
          key: metric.key,
          label: metric.label,
          value: formatPct(value),
          color: metricTargetColor(metric.key, value),
          targetHint: `${metric.label} — target ${metric.direction === 'max' ? '≤' : '≥'} ${
            metric.target
          }%`,
          hasDelta,
          delta: formatDeltaPoints(deltaValue),
          deltaIcon: deltaIcon(deltaValue),
          deltaClass: deltaColorClass(metric.key, deltaValue, this.band?.verdict ?? null),
        };
      });
    },
    /** '18 labelled · 9 lies · 9 truthful · 2 not analysed'. */
    counts() {
      if (!this.metrics) return '';
      const parts = [];
      const labelled = toNumber(this.metrics.labelled);
      if (labelled !== null) parts.push(`${labelled} labelled`);
      const incoherents = toNumber(this.metrics.incoherents);
      const truthful = toNumber(this.metrics.truthful);
      if (incoherents !== null) parts.push(`${incoherents} lies`);
      if (truthful !== null) parts.push(`${truthful} truthful`);
      const notAnalysed = toNumber(this.metrics.not_analysed);
      if (notAnalysed) parts.push(`${notAnalysed} not analysed`);
      const degraded = toNumber(this.metrics.degraded);
      if (degraded) parts.push(`${degraded} degraded`);
      const notRequested = toNumber(this.metrics.not_requested);
      if (notRequested) parts.push(`${notRequested} not requested`);
      return parts.join(' · ');
    },
  },
};
</script>

<style scoped>
.folder-run-metrics__row {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
}
.folder-run-metrics--dense .folder-run-metrics__row {
  gap: 18px;
}
.folder-run-metrics__tile {
  min-width: 92px;
}
.folder-run-metrics__value {
  font-size: 1.6rem;
  font-weight: 700;
  line-height: 1.1;
}
.folder-run-metrics--dense .folder-run-metrics__value {
  font-size: 1.35rem;
}
.folder-run-metrics__label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #757575;
  white-space: nowrap;
}
.folder-run-metrics__delta {
  font-size: 12px;
  font-weight: 600;
  min-height: 18px;
  white-space: nowrap;
}
.folder-run-metrics__counts {
  margin-top: 6px;
}
.folder-run-metrics__band {
  margin-top: 2px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}
.folder-run-metrics__empty {
  display: flex;
  align-items: center;
  min-height: 56px;
}
</style>
